import type { CSSProperties } from 'react';
import { allThemeColors, type ThemeStylesDto } from '@xshop/shared';

/**
 * The store's tokens, published as custom properties for the element engine's own markup.
 *
 * Every *node* of a tree is styled by the style engine, which compiles that node's own rule. But a
 * composite element — a cart line, a collection card, a price — draws markup of its own underneath the
 * node, and no node rule reaches inside it. Those forty-odd classes had exactly one stylesheet between
 * them, which is why a theme with no components of its own rendered browser-default blue links.
 *
 * So the engine publishes the same tokens a theme would, under one prefix of its own, and `elements.css`
 * is written against them. A store that repaints its palette repaints every composite with it.
 */
export const elementVars = (styles: ThemeStylesDto): CSSProperties => {
  const vars: Record<string, string> = {
    '--xe-font-heading': `'${styles.fonts.heading}', system-ui, sans-serif`,
    '--xe-font-body': `'${styles.fonts.body}', system-ui, sans-serif`,
    ...(styles.fonts.accent ? { '--xe-font-accent': `'${styles.fonts.accent}', system-ui, sans-serif` } : {}),
    '--xe-font-size': styles.fonts.baseSize,
    '--xe-heading-weight': String(styles.fonts.headingWeight)
  };
  // Every colour, the optional shop colours a theme left out filled in, so `var(--xe-sale)` always answers.
  for (const [name, value] of Object.entries(allThemeColors(styles.colors))) vars[`--xe-${name}`] = value;
  for (const [name, value] of Object.entries(styles.radius)) vars[`--xe-radius-${name}`] = value;
  for (const [name, value] of Object.entries(styles.spacing)) vars[`--xe-space-${name}`] = value;
  // The shell's own form and bundle chrome read these; without them an element page submits in the
  // shell's default green whatever the store's palette says (measured on the cosmetic COD form).
  vars['--primary'] = styles.colors.primary;
  vars['--primary-hover'] = styles.colors.primaryHover;
  vars['--cta-fill'] = styles.colors.primary;
  vars['--cta-glow'] = 'transparent';
  vars['--cta-ring'] = `${styles.colors.primary}33`;
  // A field the shopper is typing in is outlined in the store's primary, never the shell's green.
  vars['--border-focus'] = styles.colors.primary;
  vars['--radius-md'] = styles.radius.lg;
  // The logo's width, when the theme settings set one; the page's marks say whether it applies.
  if (styles.options?.logoWidth) vars['--xe-logo-width'] = `${styles.options.logoWidth}px`;
  // The cart drawer's width and speed, when the theme settings set them; the drawer's own stand in otherwise.
  if (styles.options?.cartWidth) vars['--xe-cart-width'] = `${styles.options.cartWidth}px`;
  if (styles.options?.cartSpeed) vars['--xe-cart-speed'] = `${styles.options.cartSpeed}ms`;
  return vars as CSSProperties;
};
